import { useNavigate } from "react-router-dom";
import type { NewsItem } from "../../../mocks/news";
import { isToday, isRecent } from "../../../lib/dateHelpers";
import { getShareUrl } from "../../../lib/share";

interface NewsCardProps {
  item: NewsItem;
  variant?: "default" | "large" | "compact" | "horizontal";
}

const getWhatsAppUrl = (articleUrl: string) => {
  const text = encodeURIComponent(articleUrl);
  return `https://api.whatsapp.com/send?text=${text}`;
};

function DateBadge({ date }: { date: string }) {
  if (isToday(date)) {
    return (
      <span className="inline-flex items-center gap-1 bg-red-500 text-white text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full shadow-sm">
        <i className="ri-flashlight-line text-[10px]" />
        Hoy
      </span>
    );
  }
  if (isRecent(date)) {
    return (
      <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full shadow-sm">
        <i className="ri-sparkling-line text-[10px]" />
        Nuevo
      </span>
    );
  }
  return null;
}

function InternationalBadge() {
  return (
    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-600 text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full border border-blue-100 shadow-sm">
      <i className="ri-earth-line text-[10px]" />
      Internacional
    </span>
  );
}

export default function NewsCard({ item, variant = "default" }: NewsCardProps) {
  const navigate = useNavigate();
  const articleUrl = `/articulo/${item.slug}`;
  const shareUrl = getShareUrl(item.slug);

  const handleCardClick = () => {
    if (item.slug) {
      navigate(articleUrl);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  // Badges de categorías secundarias. Usa la lista `secondaryCategories`
  // (derivada de tags), con fallback al campo legado `subcategory`.
  const secondaryBadges = (
    item.secondaryCategories && item.secondaryCategories.length > 0
      ? item.secondaryCategories
      : item.subcategory
        ? [item.subcategory]
        : []
  ).filter(
    (sc) =>
      sc !== item.category &&
      !(sc === "Internacional" && item.isInternational),
  );

  if (variant === "large") {
    return (
      <article
        className="relative rounded-2xl overflow-hidden group cursor-pointer h-[360px] sm:h-[440px] md:h-[520px] bg-[#0B1F3A]"
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        role="link"
        tabIndex={0}
      >
        <div className="absolute inset-0">
          <img
            src={item.image || "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-article&orientation=landscape"}
            alt={item.title}
            title={item.title}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-article&orientation=landscape";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#071529] via-[#071529]/60 to-[#071529]/20" />
        </div>
        <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20 flex items-center gap-2 pointer-events-none flex-wrap">
          <DateBadge date={item.date} />
          {item.isInternational && <InternationalBadge />}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5 md:p-6 z-20 pointer-events-none pb-8 sm:pb-10">
          <div className="flex items-center gap-1.5 mb-2 sm:mb-3 flex-wrap">
            <span className={`inline-block text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full ${item.categoryColor}`}>
              {item.category}
            </span>
            {secondaryBadges.map((sc) => (
              <span key={sc} className="inline-block text-white/80 text-[9px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full border border-white/30">
                {sc}
              </span>
            ))}

          </div>
          <h2
            className="text-white font-bold text-sm sm:text-lg md:text-xl leading-snug mb-2 sm:mb-3 group-hover:text-blue-200 transition-colors line-clamp-3"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {item.title}
          </h2>
          <p
            className="text-slate-300 text-xs sm:text-sm line-clamp-2 mb-3 sm:mb-4 hidden sm:block"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {item.excerpt}
          </p>
          <div
            className="flex items-center gap-3 text-[11px] sm:text-xs text-slate-400"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            <span>{item.date}</span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "horizontal") {
    return (
      <article
        className="flex gap-4 group cursor-pointer"
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        role="link"
        tabIndex={0}
      >
        <div className="flex-shrink-0 w-24 h-28 rounded-xl overflow-hidden bg-[#0B1F3A]">
          <img
            src={item.image || "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-article&orientation=landscape"}
            alt={item.title}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-article&orientation=landscape";
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-block text-white text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${item.categoryColor}`}>
              {item.category}
            </span>
            {secondaryBadges.map((sc) => (
              <span key={sc} className="inline-block text-slate-600 text-[8px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded-full border border-slate-300 bg-slate-50">
                {sc}
              </span>
            ))}
            {item.isInternational && item.category !== "Internacional" && item.subcategory !== "Internacional" && <InternationalBadge />}
            <DateBadge date={item.date} />
          </div>
          <h3
            className="text-[#0B1F3A] font-semibold text-sm leading-snug line-clamp-2 group-hover:text-slate-600 transition-colors"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {item.title}
          </h3>
          <p
            className="text-slate-400 text-xs mt-1"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {item.date}
          </p>
        </div>
      </article>
    );
  }

  if (variant === "compact") {
    return (
      <article
        className="bg-white rounded-2xl overflow-hidden group cursor-pointer border border-slate-100 hover:border-slate-200 transition-all hover:-translate-y-1 duration-300"
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        role="link"
        tabIndex={0}
      >
        <div className="block h-48 overflow-hidden bg-[#0B1F3A] relative">
          <img
            src={item.image || "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-article&orientation=landscape"}
            alt={item.title}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-article&orientation=landscape";
            }}
          />
          <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 flex-wrap">
            <DateBadge date={item.date} />
            {item.isInternational && <InternationalBadge />}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`inline-block text-white text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${item.categoryColor}`}>
              {item.category}
            </span>
            {secondaryBadges.map((sc) => (
              <span key={sc} className="inline-block text-slate-600 text-[8px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded-full border border-slate-300 bg-slate-50">
                {sc}
              </span>
            ))}
          </div>
          <h3
            className="text-[#0B1F3A] font-bold text-sm leading-snug line-clamp-2 group-hover:text-slate-600 transition-colors mb-2"
            style={{ fontFamily: "'Sora', sans-serif", fontSize: "0.95rem" }}
          >
            {item.title}
          </h3>
          <div
            className="flex items-center justify-between"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>{item.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={getWhatsAppUrl(shareUrl)}
                target="_blank"
                rel="noopener noreferrer"
                title="Compartir por WhatsApp"
                className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="ri-whatsapp-line text-sm" />
              </a>
              <span
                className="text-xs text-slate-500 font-semibold flex items-center gap-1 cursor-pointer hover:text-slate-700 transition-colors whitespace-nowrap"
              >
                Leer <i className="ri-arrow-right-line text-xs" />
              </span>
            </div>
          </div>
        </div>
      </article>
    );
  }

  // Default card
  return (
    <article
      className="bg-white rounded-2xl overflow-hidden group cursor-pointer border border-slate-100 hover:border-slate-200 transition-all hover:-translate-y-1.5 duration-300"
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role="link"
      tabIndex={0}
    >
      <div className="block h-80 overflow-hidden bg-[#0B1F3A] relative">
        <img
          src={item.image || "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-article&orientation=landscape"}
          alt={item.title}
          title={item.title}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-article&orientation=landscape";
          }}
        />
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 flex-wrap">
          <DateBadge date={item.date} />
          {item.isInternational && <InternationalBadge />}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className={`inline-block text-white text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full ${item.categoryColor}`}>
            {item.category}
          </span>
          {secondaryBadges.map((sc) => (
            <span key={sc} className="inline-block text-slate-600 text-[9px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full border border-slate-300 bg-slate-50">
              {sc}
            </span>
          ))}
        </div>
        <h3
          className="text-[#0B1F3A] font-bold leading-snug line-clamp-2 mb-2 group-hover:text-slate-600 transition-colors"
          style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.05rem" }}
        >
          {item.title}
        </h3>
        <p
          className="text-slate-400 text-sm line-clamp-2 mb-4 leading-relaxed"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          {item.excerpt}
        </p>
        <div className="flex items-center justify-between">
          <div
            className="flex items-center gap-2 text-xs text-slate-400"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            <span>{item.date}</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={getWhatsAppUrl(shareUrl)}
              target="_blank"
              rel="noopener noreferrer"
              title="Compartir por WhatsApp"
              className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="ri-whatsapp-line text-sm" />
            </a>
            <span
              className="text-xs text-slate-600 font-semibold flex items-center gap-1 cursor-pointer hover:text-slate-800 transition-colors whitespace-nowrap"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Leer <i className="ri-arrow-right-line text-xs" />
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}